import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Banknote,
  ChevronRight,
  Gift,
  Landmark,
  Loader2,
  Package,
  PieChart,
  PiggyBank,
  Skull,
  Sparkles,
  Tag,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { useCurrency } from '../../../../../context/CurrencyContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type MoneyBucket = {
  currency: string;
  inBankConfirmedMinor: number;
  paidNotBankConfirmedMinor: number;
  pendingUnpaidMinor: number;
  counts: {
    pendingOrders: number;
    paidOrders: number;
    payoutsUnconfirmed: number;
    bankConfirmations: number;
  };
};

type FinanceDashboardPayload = {
  generatedAt: string;
  monthLabel: string;
  buckets: MoneyBucket[];
  openBudget: null | {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    currency: string;
    balanceMinor: number;
    initialMinor: number;
  };
  inventory: {
    currency: string;
    atRetailMinor: number;
    atCostMinor: number;
  };
  lossesMonth: {
    currency: string;
    costMinor: number;
  };
  discountsMonth: {
    currency: string;
    discountsMinor: number;
    referralDiscountMinor: number;
    retentionDiscountMinor: number;
    promotionCodeDiscountMinor: number;
    checkoutLineDiscountMinor: number;
    ordersWithDiscount: number;
    ordersWithPromotion: number;
  };
  promotions: {
    activeCount: number;
  };
  profitMonth: {
    merchandiseGrossProfitMinor: number;
    netProfitMinor: number;
    from: string;
    to: string;
  };
  preorderLiability?: Array<{
    currency: string;
    depositLiabilityMinor: number;
    openPreorderCount: number;
  }>;
};

function InsightCard(props: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
  value: string;
  valueClass?: string;
  footer?: React.ReactNode;
  accent: 'brand' | 'emerald' | 'amber' | 'violet' | 'sky' | 'rose' | 'slate';
}) {
  const { icon: Icon, title, subtitle, value, valueClass, footer, accent } = props;
  const rings = {
    brand: 'from-brand-500 to-indigo-500',
    emerald: 'from-emerald-500 to-teal-500',
    amber: 'from-amber-500 to-orange-500',
    violet: 'from-violet-500 to-purple-600',
    sky: 'from-sky-500 to-cyan-500',
    rose: 'from-rose-500 to-red-500',
    slate: 'from-slate-500 to-slate-600',
  };
  const iconBg = {
    brand: 'bg-brand-100 text-brand-800 ring-brand-200/80',
    emerald: 'bg-emerald-100 text-emerald-800 ring-emerald-200/80',
    amber: 'bg-amber-100 text-amber-900 ring-amber-200/80',
    violet: 'bg-violet-100 text-violet-800 ring-violet-200/80',
    sky: 'bg-sky-100 text-sky-800 ring-sky-200/80',
    rose: 'bg-rose-100 text-rose-800 ring-rose-200/80',
    slate: 'bg-slate-100 text-slate-800 ring-slate-200/80',
  };
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-900/[0.04] sm:p-5">
      <div className={`absolute left-0 top-0 h-full w-1 bg-gradient-to-b ${rings[accent]}`} aria-hidden />
      <div className="flex items-start gap-3 pl-2">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-2 ring-inset ${iconBg[accent]}`}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}</p>
          {subtitle ? <p className="mt-0.5 text-xs text-slate-600">{subtitle}</p> : null}
          <p
            className={[
              'mt-2 break-words text-xl font-bold tabular-nums tracking-tight sm:text-2xl',
              valueClass ?? 'text-slate-900',
            ].join(' ')}
          >
            {value}
          </p>
          {footer ? <div className="mt-2 text-xs text-slate-600">{footer}</div> : null}
        </div>
      </div>
    </div>
  );
}

export default function FinanceDashboardPage() {
  const { t } = useTranslation();
  const { authorizedFetch, auth } = useAuth();
  const { currency, rates, formatMajorInCurrency, formatPrice, formatPriceFrom } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<FinanceDashboardPayload | null>(null);
  const perms = auth.permissions ?? [];
  const canProfit = perms.includes('PROFIT_REPORT:READ');
  const canBudget = perms.includes('BUDGET_PERIOD:READ');
  const canPromo = perms.includes('PROMOTION:READ');
  const canLoss = perms.includes('LOSS:READ');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/finance/dashboard`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as any)?.message || 'Failed to load finance dashboard');
        }
        const json = (await res.json()) as FinanceDashboardPayload;
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch]);

  const major = (minor: number) => minor / 100;

  const toWorkspaceMajor = useCallback(
    (amountMajor: number, fromCurrency: string): number => {
      const from = (fromCurrency || 'USD').toUpperCase();
      const to = (currency || 'USD').toUpperCase();
      const fromRate = from === 'USD' ? 1 : Number(rates[from] ?? 0);
      const toRate = to === 'USD' ? 1 : Number(rates[to] ?? 0);
      if (!Number.isFinite(fromRate) || fromRate <= 0) return 0;
      if (!Number.isFinite(toRate) || toRate <= 0) return 0;
      const usd = amountMajor / fromRate;
      return usd * toRate;
    },
    [currency, rates],
  );
  const toWorkspaceDisplay = (minor: number, fromCurrency: string): string =>
    formatPriceFrom(major(minor), (fromCurrency || 'USD').toUpperCase());

  const bucketTotals = useMemo(() => {
    const buckets = data?.buckets ?? [];
    const sumConverted = (fn: (b: MoneyBucket) => number) =>
      buckets.reduce((s, b) => s + toWorkspaceMajor(major(fn(b)), b.currency), 0);
    return {
      inBank: sumConverted((b) => b.inBankConfirmedMinor),
      paidPending: sumConverted((b) => b.paidNotBankConfirmedMinor),
      pending: sumConverted((b) => b.pendingUnpaidMinor),
      exposure: sumConverted((b) => b.inBankConfirmedMinor + b.paidNotBankConfirmedMinor + b.pendingUnpaidMinor),
      pendingOrders: buckets.reduce((s, b) => s + b.counts.pendingOrders, 0),
      paidOrders: buckets.reduce((s, b) => s + b.counts.paidOrders, 0),
    };
  }, [data?.buckets, toWorkspaceMajor]);
  const preorderLiabilityTotals = useMemo(() => {
    const rows = data?.preorderLiability ?? [];
    return rows.reduce(
      (acc, row) => {
        acc.liability += toWorkspaceMajor(row.depositLiabilityMinor / 100, row.currency);
        acc.openCount += Number(row.openPreorderCount || 0);
        return acc;
      },
      { liability: 0, openCount: 0 },
    );
  }, [data?.preorderLiability, toWorkspaceMajor]);

  const pct = (part: number, total: number) => (total > 0 ? (part / total) * 100 : 0);
  const fmtPct = (n: number) => `${n.toFixed(1)}%`;

  const inv = data?.inventory;
  const theoryGrossMinor =
    inv != null ? Math.max(0, inv.atRetailMinor - inv.atCostMinor) : 0;

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath="/workspace/finance">
      <main className="workspace-page">
        <section className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-brand-50/50 p-5 shadow-md ring-1 ring-slate-900/[0.04] sm:p-6">
          <div
            className="pointer-events-none absolute -right-12 -top-16 h-48 w-48 rounded-full bg-brand-400/20 blur-3xl"
            aria-hidden
          />
          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand-600 text-white shadow-lg shadow-brand-600/30">
                <Landmark className="h-7 w-7" aria-hidden />
              </div>
              <div>
                <h1 className="workspace-page-title">
                  {t('backoffice.finance_title', 'Finance')}
                </h1>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
                  {t(
                    'backoffice.finance_subtitle_rich',
                    'Live snapshot: budget, month-to-date profit, stock value, losses, discounts, and cashflow. Use the links below for detail pages.',
                  )}
                </p>
                {data?.monthLabel ? (
                  <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200/80">
                    <Sparkles className="h-3.5 w-3.5 text-brand-600" aria-hidden />
                    MTD metrics through {data.monthLabel}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 lg:justify-end">
              {canBudget ? (
                <Link
                  to="/workspace/budget"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-brand-300 hover:bg-brand-50/50"
                >
                  <PiggyBank className="h-4 w-4 text-brand-600" aria-hidden />
                  Budget
                  <ChevronRight className="h-4 w-4 opacity-60" aria-hidden />
                </Link>
              ) : null}
              {canProfit ? (
                <Link
                  to="/workspace/finance/profit"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50/50"
                >
                  <PieChart className="h-4 w-4 text-emerald-600" aria-hidden />
                  Profit report
                  <ChevronRight className="h-4 w-4 opacity-60" aria-hidden />
                </Link>
              ) : null}
              {canPromo ? (
                <Link
                  to="/workspace/promotions"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-amber-300 hover:bg-amber-50/50"
                >
                  <Tag className="h-4 w-4 text-amber-600" aria-hidden />
                  Coupon codes
                  <ChevronRight className="h-4 w-4 opacity-60" aria-hidden />
                </Link>
              ) : null}
            </div>
          </div>
        </section>

        {error ? (
          <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50/90 px-4 py-3 text-sm text-rose-800">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
            <span>{error}</span>
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-6 text-slate-600">
            <Loader2 className="h-5 w-5 animate-spin text-brand-600" aria-hidden />
            {t('common.loading')}
          </div>
        ) : data ? (
          <>
            <section>
              <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500 sm:text-sm">
                {t('backoffice.finance_insights_heading', 'Key figures')}
              </h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <InsightCard
                  accent="brand"
                  icon={PiggyBank}
                  title={t('backoffice.finance_tile_budget', 'Current budget balance')}
                  subtitle={
                    data.openBudget
                      ? `${data.openBudget.name} · ${data.openBudget.currency} -> ${currency}`
                      : t('backoffice.finance_no_active_budget', 'No active budget period')
                  }
                  value={
                    data.openBudget
                      ? toWorkspaceDisplay(data.openBudget.balanceMinor, data.openBudget.currency)
                      : '—'
                  }
                  valueClass={data.openBudget ? 'text-brand-800' : 'text-slate-400'}
                  footer={
                    data.openBudget ? (
                      <span className="text-slate-500">
                        {t('backoffice.finance_budget_initial', 'Initial')}:{' '}
                        {toWorkspaceDisplay(data.openBudget.initialMinor, data.openBudget.currency)}
                        {canBudget ? (
                          <Link
                            to="/workspace/budget"
                            className="ml-2 inline-flex items-center gap-0.5 font-semibold text-brand-700 hover:underline"
                          >
                            Manage <ArrowRight className="h-3 w-3" aria-hidden />
                          </Link>
                        ) : null}
                      </span>
                    ) : canBudget ? (
                      <Link
                        to="/workspace/budget"
                        className="inline-flex items-center gap-1 font-semibold text-brand-700 hover:underline"
                      >
                        Open budget workspace <ArrowRight className="h-3 w-3" aria-hidden />
                      </Link>
                    ) : null
                  }
                />

                <InsightCard
                  accent="slate"
                  icon={Banknote}
                  title={t('backoffice.finance_preorder_liability', 'Pre-order deposit liability')}
                  subtitle={t(
                    'backoffice.finance_preorder_liability_hint',
                    'Deposits held for open pre-orders; excluded from recognized revenue/profit.',
                  )}
                  value={formatMajorInCurrency(preorderLiabilityTotals.liability, currency)}
                  valueClass="text-slate-900"
                  footer={
                    <span>
                      {t('backoffice.finance_preorder_open_count', 'Open pre-orders')}:{' '}
                      <span className="font-semibold">{preorderLiabilityTotals.openCount}</span>
                    </span>
                  }
                />

                <InsightCard
                  accent="emerald"
                  icon={TrendingUp}
                  title={t('backoffice.finance_tile_profit_mtd', 'Gross profit (MTD)')}
                  subtitle={t(
                    'backoffice.finance_tile_profit_mtd_hint',
                    'Merchandise revenue − COGS, paid orders this month',
                  )}
                  value={formatPrice(major(data.profitMonth.merchandiseGrossProfitMinor))}
                  valueClass={
                    data.profitMonth.merchandiseGrossProfitMinor >= 0
                      ? 'text-emerald-700'
                      : 'text-rose-700'
                  }
                  footer={
                    <span>
                      {t('backoffice.finance_net_profit_mtd', 'Net profit (MTD)')}:{' '}
                      <span
                        className={
                          data.profitMonth.netProfitMinor >= 0
                            ? 'font-semibold text-emerald-800'
                            : 'font-semibold text-rose-800'
                        }
                      >
                        {formatPrice(major(data.profitMonth.netProfitMinor))}
                      </span>
                      {canProfit ? (
                        <Link
                          to="/workspace/finance/profit"
                          className="ml-2 inline-flex items-center gap-0.5 font-semibold text-emerald-800 hover:underline"
                        >
                          Detail <ArrowRight className="h-3 w-3" aria-hidden />
                        </Link>
                      ) : null}
                    </span>
                  }
                />

                <InsightCard
                  accent="violet"
                  icon={Package}
                  title={t('backoffice.finance_tile_inventory', 'Inventory on hand')}
                  subtitle={t(
                    'backoffice.finance_tile_inventory_hint',
                    'Remaining lot qty × list price vs unit cost (USD)',
                  )}
                  value={toWorkspaceDisplay(data.inventory.atRetailMinor, data.inventory.currency)}
                  valueClass="text-violet-900"
                  footer={
                    <div className="space-y-1">
                      <p>
                        <span className="text-slate-500">
                          {t('backoffice.finance_at_cost', 'At cost')}:
                        </span>{' '}
                        <span className="font-semibold text-slate-800">
                          {toWorkspaceDisplay(data.inventory.atCostMinor, data.inventory.currency)}
                        </span>
                      </p>
                      <p>
                        <span className="text-slate-500">
                          {t('backoffice.finance_theoretical_margin', 'Theoretical margin')}:
                        </span>{' '}
                        <span className="font-semibold text-emerald-700">
                          {toWorkspaceDisplay(theoryGrossMinor, data.inventory.currency)}
                        </span>
                      </p>
                    </div>
                  }
                />

                <InsightCard
                  accent="rose"
                  icon={Skull}
                  title={t('backoffice.finance_tile_losses', 'Stock losses (MTD)')}
                  subtitle={t(
                    'backoffice.finance_tile_losses_hint',
                    'Recorded losses this month at cost basis',
                  )}
                  value={toWorkspaceDisplay(data.lossesMonth.costMinor, data.lossesMonth.currency)}
                  valueClass={
                    data.lossesMonth.costMinor > 0 ? 'text-rose-700' : 'text-slate-600'
                  }
                  footer={
                    canLoss ? (
                      <Link
                        to="/workspace/losses"
                        className="inline-flex items-center gap-1 font-semibold text-rose-800 hover:underline"
                      >
                        {t('backoffice.finance_view_losses', 'Loss register')}
                        <ArrowRight className="h-3 w-3" aria-hidden />
                      </Link>
                    ) : (
                      <span className="text-slate-500">{t('backoffice.finance_loss_no_perm', '—')}</span>
                    )
                  }
                />

                <InsightCard
                  accent="amber"
                  icon={Gift}
                  title={t('backoffice.finance_tile_discounts', 'Discounts & coupons (MTD)')}
                  subtitle={t(
                    'backoffice.finance_tile_discounts_hint',
                    'Sum of order-level discounts on paid orders this month',
                  )}
                  value={toWorkspaceDisplay(
                    data.discountsMonth.discountsMinor,
                    data.discountsMonth.currency,
                  )}
                  valueClass="text-amber-900"
                  footer={
                    <div className="flex flex-col gap-1.5">
                      <div className="flex flex-col gap-0.5 text-[10px] leading-snug text-amber-950/90">
                        <span>
                          {t('backoffice.finance_discount_split_referral', 'Referral')}:{' '}
                          {toWorkspaceDisplay(
                            data.discountsMonth.referralDiscountMinor ?? 0,
                            data.discountsMonth.currency,
                          )}
                        </span>
                        <span>
                          {t('backoffice.finance_discount_split_retention', 'Retention')}:{' '}
                          {toWorkspaceDisplay(
                            data.discountsMonth.retentionDiscountMinor ?? 0,
                            data.discountsMonth.currency,
                          )}
                        </span>
                        <span>
                          {t('backoffice.finance_discount_split_promo_code', 'Promo codes')}:{' '}
                          {toWorkspaceDisplay(
                            data.discountsMonth.promotionCodeDiscountMinor ?? 0,
                            data.discountsMonth.currency,
                          )}
                        </span>
                        <span>
                          {t('backoffice.finance_discount_split_checkout_line', 'Checkout line')}:{' '}
                          {toWorkspaceDisplay(
                            data.discountsMonth.checkoutLineDiscountMinor ?? 0,
                            data.discountsMonth.currency,
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:gap-x-3">
                        <span>
                          {t('backoffice.finance_orders_discounted', 'Orders with discount')}:{' '}
                          <strong>{data.discountsMonth.ordersWithDiscount}</strong>
                        </span>
                        <span>
                          {t('backoffice.finance_orders_promo', 'With promo code')}:{' '}
                          <strong>{data.discountsMonth.ordersWithPromotion}</strong>
                        </span>
                        {canPromo ? (
                          <Link
                            to="/workspace/promotions"
                            className="inline-flex items-center gap-1 font-semibold text-amber-900 hover:underline"
                          >
                            {t('backoffice.finance_manage_promos', 'Manage codes')}
                            <ArrowRight className="h-3 w-3" aria-hidden />
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  }
                />

                <InsightCard
                  accent="sky"
                  icon={Tag}
                  title={t('backoffice.finance_tile_active_promos', 'Active promotion codes')}
                  subtitle={t(
                    'backoffice.finance_tile_active_promos_hint',
                    'Not expired, still enabled — redeem at checkout',
                  )}
                  value={String(data.promotions.activeCount)}
                  valueClass="text-sky-900"
                  footer={
                    canPromo ? (
                      <p className="text-slate-600">
                        {t(
                          'backoffice.finance_promo_workspace_blurb',
                          'Create codes, set expiry and caps, restrict to a customer, and deactivate when done — all under Promotions.',
                        )}
                      </p>
                    ) : (
                      t('backoffice.finance_promo_no_perm', 'Requires promotion permissions.')
                    )
                  }
                />
              </div>
            </section>

            <section className="glass-panel p-5 sm:p-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-slate-700" aria-hidden />
                  <h2 className="text-base font-semibold text-slate-900">
                    {t('backoffice.finance_cashflow_heading', 'Cashflow & exposure')}
                  </h2>
                </div>
                {data.buckets.length > 0 ? (
                  <span className="rounded-full bg-slate-900/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                    {bucketTotals.paidOrders} paid · {bucketTotals.pendingOrders} pending
                  </span>
                ) : null}
              </div>

              {data.buckets.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-8 text-center">
                  <Banknote className="mx-auto h-10 w-10 text-slate-400" aria-hidden />
                  <p className="mt-3 text-sm font-medium text-slate-700">
                    {t(
                      'backoffice.finance_no_cashflow_buckets',
                      'No bank confirmations, Stripe payouts, or pending orders yet — cashflow breakdown will appear here.',
                    )}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    {t(
                      'backoffice.finance_no_cashflow_hint',
                      'Figures above still reflect inventory, budget, and MTD profit.',
                    )}
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                        {t('backoffice.finance_exposure_total', 'Total exposure')}
                      </p>
                      <p className="mt-1 text-xl font-bold tabular-nums text-slate-900">
                        {formatMajorInCurrency(bucketTotals.exposure, currency)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/50 p-4 shadow-sm">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-800">
                        {t('backoffice.finance_in_bank', 'In bank')}
                      </p>
                      <p className="mt-1 text-xl font-bold tabular-nums text-emerald-900">
                        {formatMajorInCurrency(bucketTotals.inBank, currency)}
                      </p>
                      <p className="mt-1 text-xs text-emerald-800/90">
                        {fmtPct(pct(bucketTotals.inBank, bucketTotals.exposure))}{' '}
                        {t('backoffice.finance_of_exposure', 'of exposure')}
                      </p>
                    </div>
                    <div className="rounded-xl border border-sky-200/80 bg-sky-50/50 p-4 shadow-sm">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-sky-900">
                        {t('backoffice.finance_paid_unreconciled', 'Paid, not bank-confirmed')}
                      </p>
                      <p className="mt-1 text-xl font-bold tabular-nums text-sky-900">
                        {formatMajorInCurrency(bucketTotals.paidPending, currency)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-amber-200/80 bg-amber-50/50 p-4 shadow-sm">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-amber-950">
                        {t('backoffice.finance_pending_unpaid', 'Pending unpaid')}
                      </p>
                      <p className="mt-1 text-xl font-bold tabular-nums text-amber-950">
                        {formatMajorInCurrency(bucketTotals.pending, currency)}
                      </p>
                    </div>
                  </div>

                  {data.buckets.map((b) => {
                    const total =
                      b.inBankConfirmedMinor + b.paidNotBankConfirmedMinor + b.pendingUnpaidMinor;
                    const inBankPct = pct(b.inBankConfirmedMinor, total);
                    const paidPct = pct(b.paidNotBankConfirmedMinor, total);
                    const pendingPct = Math.max(0, 100 - inBankPct - paidPct);
                    return (
                      <div
                        key={b.currency}
                        className="rounded-2xl border border-slate-200/90 bg-gradient-to-br from-white to-slate-50/80 p-4 shadow-sm sm:p-5"
                      >
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <h3 className="font-semibold text-slate-900">
                            {b.currency}{' '}
                            {t('backoffice.finance_cashflow_health', 'cashflow')}
                          </h3>
                          <span className="text-xs font-medium text-slate-600">
                            {b.counts.paidOrders} paid · {b.counts.pendingOrders} pending
                          </span>
                        </div>
                        <div className="mb-2 flex items-center justify-between text-xs text-slate-600">
                          <span>{t('backoffice.finance_balance_mix', 'Balance composition')}</span>
                          <span className="font-semibold">
                            {formatMajorInCurrency(major(total), b.currency)}
                          </span>
                        </div>
                        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
                          <div
                            className="h-full min-w-0 shrink-0 bg-emerald-500 transition-all"
                            style={{ width: `${inBankPct}%` }}
                            title={`In bank ${fmtPct(inBankPct)}`}
                          />
                          <div
                            className="h-full min-w-0 shrink-0 bg-sky-500 transition-all"
                            style={{ width: `${paidPct}%` }}
                            title={`Paid ${fmtPct(paidPct)}`}
                          />
                          <div
                            className="h-full min-w-0 shrink-0 bg-amber-400 transition-all"
                            style={{ width: `${pendingPct}%` }}
                            title={`Pending ${fmtPct(pendingPct)}`}
                          />
                        </div>
                        <div className="mt-2 flex flex-wrap gap-3 text-[11px] font-semibold">
                          <span className="text-emerald-700">
                            {t('backoffice.finance_legend_bank', 'In bank')} {fmtPct(inBankPct)}
                          </span>
                          <span className="text-sky-700">
                            {t('backoffice.finance_legend_paid', 'Paid')} {fmtPct(paidPct)}
                          </span>
                          <span className="text-amber-800">
                            {t('backoffice.finance_legend_pending', 'Pending')} {fmtPct(pendingPct)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        ) : null}
      </main>
    </MainLayout>
  );
}
